import {addErrorMessage, addViolation, logoutUser} from 'actions';
import { history } from 'utils';
import {appRoutes} from '../../constants';
import i18n from '../../i18n';

/**
 * Backend business-rule violations (DrawWorkflowService, CompetitionDataPersister,
 * OfficialResultsConfirmationService, …) throw a STABLE KEY as their HttpException
 * message (e.g. 'errors.draw.onlyApprovedCanReopen') instead of an English
 * sentence — the same "backend owns the key, frontend owns the wording"
 * contract ConfirmOfficialResults.js already uses for confirmation failures.
 * `defaultValue` falls back to the raw string for any message that ISN'T one
 * of these keys (every other exception in the app, untouched), so this never
 * regresses text that was already being shown as-is.
 */
const translateBackendMessage = text => i18n.t(text, { defaultValue: text });

// Every API call's own `.catch()` dispatches errorRequestHandler(error)
// independently (this IS the single centralized place — ~90 action files
// all funnel through it), so a page with several requests in flight when
// the JWT expires used to fire this function once PER failing request:
// several duplicate "JWT token expired" / "JWT token not found" toasts
// (the raw, untranslated Lexik JWT bundle message) plus several redundant
// logoutUser() dispatches. A session expiring is ONE application-level
// event, not one per request, so this guard collapses every 401 that
// arrives while a session-expiry is already being handled into a no-op.
// Reset on the next successful login (see loginAction.js) so a LATER
// expiry is handled again.
let sessionExpirationInProgress = false;

export const resetSessionExpirationGuard = () => {
    sessionExpirationInProgress = false;
};

/*
 * A REQUEST THAT NEVER REACHED THE SERVER.
 *
 * Everything below reads the message out of `data`, which is the SERVER's
 * answer. When the network is gone there is no answer at all: axios rejects
 * with no `.response`, so `status` is undefined and `data` is `{}` — and this
 * function dispatched NOTHING.
 *
 * That is the same shape of silence the pilot found in 2026 and the note above
 * describes, one case further out. Measured at a mat on 30 Sep 2026: with the
 * network down a secretary typed a kata score, the field went blank, and there
 * was not one message on the screen. The score was never sent; nothing said so.
 *
 * ONE message while the connection is down, not one per attempt. The workspace
 * polls every ten seconds and the display faster still; a toast per failed poll
 * is the noise `venue-resilience.admin.spec.ts` exists to prevent, and it would
 * bury the very message that matters. Same shape as the 401 guard above, reset
 * by the next request that gets through.
 */
let connectionLossReported = false;

export const resetConnectionLossGuard = () => {
    connectionLossReported = false;
};

/** True for a rejection that never got an HTTP answer. */
const isConnectionLoss = (failure, response) =>
    undefined === (response || {}).status
    && !!failure
    && !failure.data
    && ('ERR_NETWORK' === failure.code
        || 'ECONNABORTED' === failure.code
        || !!failure.request
        || 'Network Error' === failure.message);

const handleConnectionLost = () => {
    if (connectionLossReported) {
        return function () {};
    }
    connectionLossReported = true;

    return function (dispatch) {
        dispatch(addErrorMessage(i18n.t(
            'errors.connection.lost',
            'No connection to the server — the action was not saved. It will work again once the connection is back; nothing already recorded is lost.',
        )));
    };
};

const handleSessionExpired = () => {
    if (sessionExpirationInProgress) {
        return function () {};
    }
    sessionExpirationInProgress = true;

    return function (dispatch) {
        // The backend's own wording ("JWT Token not found", "Expired JWT
        // Token", …) is a third-party library message, not one of our
        // stable translation keys — show one fixed, localized message
        // instead of relaying it verbatim.
        dispatch(addErrorMessage(i18n.t('errors.session.expired', 'Your session has expired. Please sign in again.')));
        dispatch(logoutUser());
    };
};

/**
 * Accepts EITHER shape, because callers hand over both.
 *
 * Most actions run their rejection through `transformHttpResponse` first,
 * which produces an Error carrying `.status` and `.data`. Some hand over the
 * raw axios rejection instead — and axios puts those under `.response`, so
 * destructuring them off the error itself yielded `status: undefined` and
 * `data: {}`. Since the message shown is read out of `data`, those callers
 * dispatched NOTHING: the request failed and the screen said nothing at all.
 *
 * The first competition pilot found it at a mat. A secretary recorded a flag
 * winner, pressed «Підтвердити результат», and watched nothing happen; the
 * backend had refused three times with 403 errors.category.closed. It applied
 * to every protocol command and every operations mutation.
 *
 * Unwrapping here rather than at each call site because this is the one place
 * that already knows how a failure becomes something an operator sees — and
 * because the next caller to forget will be handled too.
 */
const unwrap = error =>
    (error && error.response && undefined === error.status) ? error.response : error;

/*
 * A REFUSED DOWNLOAD (18 Oct 2026: «щоб користувач теж бачив повідомлення, а
 * то зараз тільки можна в консолі побачити»). A PDF or Excel request asks for
 * a Blob, so axios hands the server's JSON refusal over as a Blob too — e.g.
 * «У змаганнях немає індивідуальних учасників для ранжування» on «Звіт
 * керівника делегації». Read out of `data` as it was, it had no `message`,
 * and nothing was shown. Every download funnels through here, so it is read
 * here once: the Blob's text, as JSON when it is JSON, then handled as any
 * other answer.
 */
const isBlob = data => 'undefined' !== typeof Blob && data instanceof Blob;

const blobText = blob => ('function' === typeof blob.text
    ? blob.text()
    : new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error);
        reader.readAsText(blob);
    }));

const parsedBlob = blob => blobText(blob)
    .then(text => {
        try {
            return JSON.parse(text);
        } catch (e) {
            return {};
        }
    })
    .catch(() => ({}));

export const errorRequestHandler = failure => {
    const response = unwrap(failure);
    const {status, data = {}} = response || {};

    if (isBlob(data)) {
        return function (dispatch) {
            return parsedBlob(data).then(parsed => dispatch(errorRequestHandler({ ...response, data: parsed || {} })));
        };
    }

    if (401 === status) {
        return handleSessionExpired();
    }

    // Before anything reads `data`: there is no `data` when the request never
    // arrived, and saying nothing is the one answer a mat cannot use.
    if (isConnectionLoss(failure, response)) {
        return handleConnectionLost();
    }

    const additionalActions = [];
    const { title, violations, message, detail } = data;
    if (500 === status) {
        history.push(appRoutes.ERROR_PAGE_500);
    }
    violations && additionalActions.push(addViolation(violations));
    message && additionalActions.push(addErrorMessage(translateBackendMessage(message)));
    title && additionalActions.push(addErrorMessage(translateBackendMessage(title) + (detail && '. ' + detail)));
    return function(dispatch) {
        additionalActions.forEach(a => dispatch(a));
    };
};
